import { motion } from "framer-motion";
import { Mic, CalendarClock, Route, Sparkles, ArrowRight } from "lucide-react";
import { HALOFLOW_URL } from "@/lib/brand";

const CAPABILITIES = [
  { icon: Mic, title: "Voice Intake", desc: "Natural AI voice agents answer, qualify, and capture every inbound conversation." },
  { icon: CalendarClock, title: "Scheduling", desc: "Autonomous booking and reminders that cut no-shows around the clock." },
  { icon: Route, title: "Lead Routing", desc: "Intelligent triage routes the right opportunity to the right human, instantly." },
];

export default function HaloFlow() {
  return (
    <section id="haloflow" className="relative py-24 md:py-32 bg-ink text-white overflow-hidden" data-testid="haloflow-section">
      <div className="absolute inset-0 blueprint-grid-dark" />
      <img
        src="https://images.unsplash.com/photo-1768806506237-7c96dff8bf26?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
        alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-[0.14]" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/40" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-16 lg:grid-cols-2 lg:items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 border border-slate-700 px-3 py-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-ember">
              <Sparkles className="h-3.5 w-3.5" /> The AI Engine
            </div>
            <h2 className="mt-6 font-heading font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl">
              HaloFlow.
            </h2>
            <p className="mt-5 text-lg text-slate-300 max-w-xl leading-relaxed">
              The proprietary AI voice and agent stack powering the OTBMG ecosystem.
            </p>
            <div className="mt-8 border-l-2 border-ember pl-6">
              <p className="font-heading font-bold text-2xl text-white">Automate the Routine,<br />Elevate the Human.</p>
              <p className="mt-4 text-slate-300 leading-relaxed max-w-xl">
                HaloFlow handles 24/7 repetitive execution — intake, scheduling, and lead routing — so our human
                experts are freed to focus on high-level commercial strategy and creative judgment.
              </p>
            </div>
            <a href={HALOFLOW_URL} target="_blank" rel="noreferrer" data-testid="haloflow-cta"
               className="mt-8 group inline-flex items-center gap-2 bg-ember text-white px-7 py-4 text-sm font-semibold hover:bg-ember-dark transition-colors">
              Explore HaloFlow
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>

          <div className="grid gap-5">
            {CAPABILITIES.map((c, i) => (
              <motion.div key={c.title}
                initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`haloflow-cap-${i}`}
                className="flex items-start gap-5 border border-slate-700 bg-white/5 backdrop-blur-sm p-6 hover:border-ember transition-colors">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center bg-ember text-white">
                  <c.icon className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-heading font-bold text-lg">{c.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-300 leading-relaxed">{c.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
